/**
 * Ascent → route field resolution.
 *
 * Every ascent doc carries a denormalized snapshot of its route's display
 * fields (`routeName`, `routeDifficulty`, `routeHoldColor`, `routeDiscipline`)
 * captured at the moment the ascent was logged. Stats, feeds, and profile
 * views read these directly so they keep working even when the route gets
 * archived later and falls out of the active-only routes listener.
 *
 * `resolveAscentRoute` returns a route-shaped subset (`{id, name, difficulty,
 * holdColor, discipline}`) using the snapshot when present and falling back
 * to a lookup against the live routes array for any pre-snapshot ascent the
 * backfill hasn't covered yet. Returns null if neither path resolves.
 */

export function resolveAscentRoute(ascent, routes) {
  if (!ascent) return null;
  const hasSnapshot = ascent.routeDifficulty !== undefined || ascent.routeName !== undefined;
  if (hasSnapshot) {
    return {
      id: ascent.routeId,
      name: ascent.routeName ?? "",
      difficulty: ascent.routeDifficulty ?? null,
      holdColor: ascent.routeHoldColor ?? null,
      discipline: ascent.routeDiscipline || "boulder",
    };
  }
  if (!routes) return null;
  return routes.find(r => r.id === ascent.routeId) || null;
}

/**
 * The route a climb-LOG entry (`climbLogs/{uid}_{date}.climbs[cid]`) is shown
 * with — the friends' activity feeds' "single new route" detail.
 *
 * A log entry is NOT an ascent doc: it snapshots `difficulty`, `holdColor` and
 * `discipline` but never the route's NAME. `resolveAscentRoute` must not be
 * handed one (its snapshot test would take the entry as a complete snapshot and
 * return a nameless route, so the feed read "Ismeretlen szín Piros" instead of
 * the name). Here the name — the one field the entry lacks — comes from the live
 * route, and the grade fields keep the entry's snapshot, so a regrade since
 * does not rewrite the day. A route that has since left the live list (archived)
 * stays nameless and is described by its hold colour and grade, like everywhere.
 */
export function resolveLogEntryRoute(entry, routes) {
  if (!entry) return null;
  const live = routes?.find(r => r.id === entry.routeId) || null;
  return {
    id: entry.routeId,
    name: live?.name ?? "",
    difficulty: entry.difficulty ?? live?.difficulty ?? null,
    holdColor: entry.holdColor ?? live?.holdColor ?? null,
    discipline: entry.discipline || live?.discipline || "boulder",
  };
}
